'use client';

import { BrowserRecordingArtifactMetadataSchema } from '@terra/shared-contracts-artifact';
import { PlayIcon } from '@terra/ui-icons';
import { Box, css, VStack } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { Button } from '../../../../components/button';
import { IconButton } from '../../../../components/icon-button';
import { MetaDefault } from '../../../../components/typography';
import { useBrowserRecordingUrl } from '../hooks/useBrowserRecordingUrl';
import { useRecordingArtifact } from '../hooks/useRecordingArtifact';

const RECORDING_PLAYBACK_RATE = 2;

const recordingFrameStyle = css({
  display: 'block',
  width: '100%',
  maxHeight: '360px',
  aspectRatio: '16 / 9',
  objectFit: 'contain',
  borderRadius: 'md',
  borderWidth: '1px',
  borderColor: 'border',
  bg: 'bg.muted',
});

type BrowserRecordingPlayerProps = {
  toolCallId: string;
};

export function BrowserRecordingPlayer({
  toolCallId,
}: BrowserRecordingPlayerProps) {
  const t = useTranslations('designSystem.chat.browserResult');
  const tCommon = useTranslations('designSystem.common');
  const [isPlayRequested, setIsPlayRequested] = useState(false);
  const artifact = useRecordingArtifact(toolCallId);
  const parsedMetadata = BrowserRecordingArtifactMetadataSchema.safeParse(
    artifact?.metadata,
  );
  const { recordingUrl, isLoading } = useBrowserRecordingUrl({
    artifactId:
      parsedMetadata.success && isPlayRequested ? artifact?.id : undefined,
    artifactCreatedAt: artifact?.createdAt,
  });

  if (!parsedMetadata.success) {
    return null;
  }

  const { recordingThumbnail } = parsedMetadata.data;
  const requestPlay = () => setIsPlayRequested(true);
  const isRecordingUnavailable = isPlayRequested && !isLoading && !recordingUrl;
  const shouldShowThumbnail = !recordingUrl && !!recordingThumbnail;
  const shouldShowPlayButton = !recordingUrl && !isRecordingUnavailable;
  const shouldShowStandalonePlayButton =
    !recordingThumbnail && shouldShowPlayButton;

  return (
    <VStack alignItems="flex-start" gap={1} width="100%" mt={3}>
      <MetaDefault color="fg.muted">{t('recording')}</MetaDefault>
      {recordingUrl ? (
        <video
          className={recordingFrameStyle}
          src={recordingUrl.signedUrl}
          poster={recordingThumbnail}
          controls
          autoPlay
          onLoadedMetadata={(event) => {
            event.currentTarget.playbackRate = RECORDING_PLAYBACK_RATE;
          }}
          aria-label={t('recording')}
        />
      ) : null}
      {shouldShowThumbnail ? (
        <Box position="relative" width="100%">
          <img
            className={recordingFrameStyle}
            src={recordingThumbnail}
            alt={t('recording')}
          />
          {shouldShowPlayButton ? (
            <Box
              position="absolute"
              inset="0"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <IconButton
                variant="solid"
                size="md"
                borderRadius="full"
                loading={isLoading}
                onClick={requestPlay}
                aria-label={tCommon('play')}
              >
                <PlayIcon size={18} />
              </IconButton>
            </Box>
          ) : null}
        </Box>
      ) : null}
      {shouldShowStandalonePlayButton ? (
        <Button
          variant="outline"
          size="xs"
          loading={isLoading}
          onClick={requestPlay}
        >
          <PlayIcon size={12} />
          {tCommon('play')}
        </Button>
      ) : null}
      {isRecordingUnavailable ? (
        <MetaDefault color="fg.muted">{t('recordingUnavailable')}</MetaDefault>
      ) : null}
    </VStack>
  );
}
